import React, { ComponentType, ReactNode } from 'react'
import { IChoice, ImageWrapper, OptionContent } from '../../assets/styles'
import { QuestionChoice } from '@mission.io/question-toolkit'
import { ReviewTag } from '../../stories/multiple-choice/Student'
import { getQuestionOptionIndex, Image } from '../../utils'
import OptionalMarkdown from '../Markdown/OptionalMarkdown'

import 'styled-components/macro'
import { styled } from 'styled-components'
import { ClassResponse } from '../../model/general'
import { _tempTheme } from '../../utils/tempTheme'

interface ChoiceBoxProps {
	isReview?: boolean
	isRow?: boolean
	selected?: boolean
	onClick?: () => void
	choice: QuestionChoice
	textToSpeech?: ComponentType<{
		children: ReactNode
		onStartSpeech?: (event: TouchEvent | MouseEvent) => void
	}>
	classResponse?: ClassResponse | null
	parentRef?: React.MutableRefObject<HTMLElement | undefined>
	optionScore: number
	children?: JSX.Element
	type?: 'MULTIPLE_CHOICE' | 'MULTIPLE_SELECT'
	// Whether to show an indicator for which option this is, based on the `index`. e.g. A, B, C etc
	showOptionIndicator?: boolean
	// The index of this choice. Used to optionally show an option indicator based on `showOptionIndicator`
	index: number
}

//Component that render a  ChoiceBox
export default function ChoiceBox(props: ChoiceBoxProps): JSX.Element {
	const {
		isReview = false,
		selected = false,
		onClick,
		choice,
		textToSpeech: TextToSpeech,
		classResponse,
		parentRef,
		optionScore,
		children,
		isRow = false,
		type = 'MULTIPLE_CHOICE',
		index,
		showOptionIndicator = true,
	} = props

	const correct = choice.answer

	return (
		<ChoiceBoxWrapper
			data-testid="choice-box"
			$correct={correct}
			$isReview={isReview}
			selected={selected}
			onClick={onClick}
			$isRow={isRow}>
			{isReview && (
				<ReviewTag
					parentRef={parentRef}
					selected={selected}
					correct={correct}
					type={type}
				/>
			)}
			{showOptionIndicator && (
				<OptionIndicator className="notranslate">
					{getQuestionOptionIndex(index)}
				</OptionIndicator>
			)}
			<SelectedOption
				$hasText={Boolean(choice.text)}
				$hasImage={Boolean(choice.media?.length)}
				$isRow={isRow}
				$extraPadding={isReview && !showOptionIndicator}>
				<ImageWrapper
					$hasText={Boolean(choice.text)}
					$isClassResponse={!!classResponse}
					$hasImage={Boolean(choice.media?.length)}>
					<Image media={choice.media} className="max-h-40" />
				</ImageWrapper>
				<OptionContent className={!choice.media ? 'w-full' : 'ml-2'}>
					<OptionalMarkdown textToSpeech={TextToSpeech}>
						{choice.text}
					</OptionalMarkdown>
				</OptionContent>
				{children}
				{classResponse && (
					<ScoreWrapper>
						<div className="content">{`${Math.trunc(optionScore * 100)}%`}</div>
					</ScoreWrapper>
				)}
			</SelectedOption>
		</ChoiceBoxWrapper>
	)
}

const SelectedOption = styled.div<{
	$hasText: boolean
	$hasImage: boolean
	$isRow: boolean
	// Add extra left padding. Helps move content out of the way of the tag
	$extraPadding: boolean
}>`
	flex: 1;
	box-sizing: border-box;
	height: 100%;
	display: flex;
	align-items: center;
	border-radius: ${_tempTheme.spacing1};
	justify-content: ${({ $hasText, $hasImage, $isRow }) =>
		$isRow ? 'space-between' : $hasImage && $hasText ? 'flex-start' : 'center'};
	${({ $hasImage, $isRow, $extraPadding }) => {
		let style = `padding: ${
			$hasImage || $isRow ? _tempTheme.spacing2 : _tempTheme.spacing4
		};`
		if ($extraPadding) {
			style += `padding-left: ${
				$hasImage || $isRow ? _tempTheme.spacing8 : _tempTheme.spacing10
			};`
		}
		return style
	}};

	.option {
		margin: ${_tempTheme.spacing2};
		width: 23px;
		height: 23px;
		min-width: 23px !important;
		box-sizing: border-box;
		border-radius: ${_tempTheme.spacing1};
		display: flex;
	}
	.selected {
		background: ${({ theme }) => theme.white};
	}
`

const ChoiceBoxWrapper = styled.li<IChoice>`
	display: flex;
	position: relative;
	color: ${({ theme }) => theme.black};
	text-align: center;
	list-style-type: none;
	border-radius: ${_tempTheme.spacing1};
	cursor: pointer;
	break-inside: avoid;
	overflow: hidden;

	&:not(:last-child) {
		margin-bottom: ${({ $isRow, theme }) => $isRow && theme.spacing};
	}
	.tag {
		box-sizing: border-box;
		position: absolute;
		color: ${({ theme }) => theme.white};
		padding: 4px;
		z-index: 1;
		width: fit-content;
		height: fit-content;
		border-bottom-right-radius: 50%;
		svg {
			cursor: pointer;
		}
	}

	${({ selected, $isReview: isReview, $correct: correct, theme }) => {
		let borderColor
		let background = theme.lightestPrimaryColor
		if (selected && isReview && !correct) {
			borderColor = theme.error
		}
		if (selected && !isReview) {
			borderColor = theme.secondaryColor
			background = theme.lighterPrimaryColor
		}
		if (isReview && correct) {
			borderColor = theme.success
		}
		return `
		background-color: ${background};
		${
			borderColor
				? `
					&:after {
						content: '';
						position: absolute;
						top: 0;
						right: 0;
						left: 0;
						bottom: 0;
						border: 4px solid ${borderColor};
						border-radius: ${_tempTheme.spacing1}
						z-index: -1;
						pointer-events: none;
					}
					.tag {
						background-color: ${borderColor};
					}
				`
				: ``
		}
		${SelectedOption} .option {
			border: 3px solid ${isReview && selected ? borderColor : theme.primaryColor};
			color: ${isReview && selected ? borderColor : theme.primaryColor};	
		}
	
		`
	}}
`

const ScoreWrapper = styled.div`
	color: ${({ theme }) => theme.white};
	height: 100%;
	display: flex;
	align-items: center;
	display: flex;
	justify-content: center;
	margin-left: ${_tempTheme.spacing2};

	.content {
		height: fit-content;
		min-width: 2.2em;
		margin: auto;
		border-radius: ${_tempTheme.spacing1};
		padding: ${_tempTheme.spacing2};
		background: ${({ theme }) => theme.success};
	}
`

// Container for the option indicator, aka the letter (A, B, C, etc) for the option
const OptionIndicator = styled.div`
	background-color: ${({ theme }) => theme.lightSecondaryColor};
	color: ${({ theme }) => theme.white};
	padding: ${_tempTheme.spacing2};
	font-family: ${({ theme }) => theme.headerFontFamily};
	font-size: 1.25em;
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: ${_tempTheme.spacing1} 0 0 ${_tempTheme.spacing1};
`
